import { useMemo, useState } from 'react'
import { collectHandicapReview, resolveAllowSelfReported } from '../lib/handicapEntry.js'
import { ALLOWANCE_OPTIONS, DEFAULT_ALLOWANCE } from '../lib/handicap.js'
import { updateTournamentInfo, updateRegistration, snapshotNetHandicaps } from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'

// STEP 5 + ALLOWANCE STEP — "who self-reported" review plus per-player
// allowance % override. Reads registrations (the only place source flags +
// per-player overrides live); players on manually-created teams without a
// registration are not surfaced. Allowance uses an iOS-style tap-a-pill →
// %-grid interaction; the underlying wiring is unchanged.
const GREEN = 'var(--ge-primary)'
const GOLD = 'var(--ge-primary)'
const CREAM = '#FFFFFF'

const FILTERS = [
  { key: 'all',  label: 'All' },
  { key: 'ghin', label: '(GH)#' },
  { key: 'self', label: 'Self-reported' },
  { key: 'none', label: 'No index' },
]

const BADGE = {
  ghin: { cls: 'pill-green', label: '(GH)#' },
  self: { cls: 'pill-amber', label: 'Self-reported' },
  none: { cls: 'pill-gray',  label: 'No index' },
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Brand %-grid picker. allowDefault adds a "Default" tile that clears a
// per-player override (onPick(null)). Options mirror ALLOWANCE_OPTIONS.
function PctGridPicker({ open, title, subtitle, current, allowDefault, onPick, onClose }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div className="rounded-2xl w-full max-w-sm p-5" style={{ backgroundColor: GREEN, border: `1px solid ${GOLD}55` }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h4 className="text-lg font-bold" style={{ color: GOLD }}>{title}</h4>
          <button onClick={onClose} className="text-sm px-1" style={{ color: `${CREAM}aa` }}>✕</button>
        </div>
        {subtitle ? <p className="text-xs mb-3" style={{ color: `${CREAM}99` }}>{subtitle}</p> : null}
        <div className="grid grid-cols-4 gap-2">
          {allowDefault ? (
            <button onClick={() => onPick(null)}
              className="col-span-4 rounded-xl py-2.5 text-sm font-bold uppercase tracking-wider"
              style={{ backgroundColor: `${CREAM}14`, color: CREAM, border: `1px solid ${CREAM}33` }}>
              Default (inherit event %)
            </button>
          ) : null}
          {ALLOWANCE_OPTIONS.map((p) => {
            const active = Number(current) === p
            return (
              <button key={p} onClick={() => onPick(p)}
                className="rounded-xl py-3 text-base font-extrabold transition"
                style={active
                  ? { backgroundColor: GOLD, color: GREEN }
                  : { backgroundColor: `${GOLD}1a`, color: GOLD, border: `1px solid ${GOLD}44` }}>
                {p}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function HandicapReviewTab({ tournamentId, tournament }) {
  const [filter, setFilter] = useState('all')
  const [picker, setPicker] = useState(null) // null | {kind:'event'} | {kind:'player', row}
  const info = tournament?.info || {}
  const { rows, counts } = useMemo(
    () => collectHandicapReview(tournament?.registrations),
    [tournament?.registrations],
  )
  const allowSelf = resolveAllowSelfReported(info)
  const eventAllowance = Number.isFinite(Number(info.handicapAllowance)) ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE
  const locked = !!info.flightsLocked
  const handicapOn = (info.handicapMode || 'none') !== 'none'

  // Refresh the net snapshot after an allowance change so a locked leaderboard
  // reflects it immediately. Best-effort — never blocks the edit.
  const reSnapshot = () => {
    if (locked && handicapOn) snapshotNetHandicaps(tournamentId).catch(() => {})
  }

  const setEventDefault = async (pct) => {
    await updateTournamentInfo(tournamentId, { handicapAllowance: Number(pct) })
    reSnapshot()
  }

  // Patch just the one player's allowance via a nested-path update (no array
  // rewrite). null clears the override (player inherits the event default).
  const setPlayerAllowance = async (row, value) => {
    await updateRegistration(tournamentId, row.regId, { [`playerHandicaps/${row.playerIndex}/allowance`]: value })
    reSnapshot()
  }

  const resetAll = async () => {
    const overridden = rows.filter((r) => r.allowance !== null)
    if (overridden.length === 0) return
    await Promise.all(overridden.map((r) =>
      updateRegistration(tournamentId, r.regId, { [`playerHandicaps/${r.playerIndex}/allowance`]: null }),
    ))
    reSnapshot()
  }

  const onPick = async (val) => {
    const p = picker
    setPicker(null)
    if (!p) return
    if (p.kind === 'event') { if (val != null) await setEventDefault(val) }
    else if (p.kind === 'player') { await setPlayerAllowance(p.row, val == null ? null : Number(val)) }
  }

  const shown = filter === 'all'
    ? rows
    : rows.filter((r) => (r.source || 'none') === filter)

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="font-bold text-lg">Handicap Review</h3>
        <p className="text-xs text-gray-500 mb-3">
          Per-player handicap source and allowance, from registrations. Bulk-imported team
          handicaps with no per-player index show as “No index.”
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="pill-green">{counts.ghin} (GH)#-verified</span>
          <span className="pill-amber">{counts.self} self-reported</span>
          <span className="pill-gray">{counts.none} no index</span>
          <span className="text-gray-500 self-center">· {counts.total} player{counts.total === 1 ? '' : 's'}</span>
        </div>
        {!allowSelf ? (
          <p className="text-xs mt-3 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
            ⚠ This event requires (GH)#-verified handicaps — self-reported indexes are excluded from net scoring.
          </p>
        ) : null}
      </div>

      {/* Big "Apply to All" default — tap to open the % grid. */}
      <button onClick={() => setPicker({ kind: 'event' })}
        className="w-full rounded-2xl px-6 py-5 flex items-center justify-between gap-4 text-left transition hover:brightness-110"
        style={{ backgroundColor: GREEN, border: `1.5px solid ${GOLD}` }}>
        <div>
          <div className="text-xl font-extrabold tracking-wide" style={{ color: GOLD }}>DEFAULT ALLOWANCE</div>
          <div className="text-sm mt-1" style={{ color: CREAM }}>Applies to every player without an override</div>
          <span className="inline-flex items-center gap-1 mt-2.5 text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1"
                style={{ backgroundColor: `${GOLD}22`, color: GOLD }}>Tap to change ›</span>
        </div>
        <div className="flex items-baseline gap-0.5 shrink-0">
          <span className="text-5xl font-extrabold leading-none" style={{ color: GOLD }}>{eventAllowance}</span>
          <span className="text-2xl font-bold" style={{ color: GOLD }}>%</span>
        </div>
      </button>

      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-1 rounded-lg p-1 w-fit"
               style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
            {FILTERS.map((f) => {
              const active = filter === f.key
              return (
                <button key={f.key} type="button" onClick={() => setFilter(f.key)}
                  className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
                  style={active ? { backgroundColor: 'var(--ge-primary)', color: '#FFFFFF' } : { backgroundColor: 'transparent', color: '#374151' }}>
                  {f.label}
                </button>
              )
            })}
          </div>
          <button className="btn-ghost text-xs" onClick={resetAll}>↺ Reset all to default</button>
        </div>

        {shown.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">No players match this filter.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-600 font-bold tracking-wide">
                <tr>
                  <th className="text-left py-1">Player</th>
                  <th className="text-left py-1">Team</th>
                  <th className="text-right py-1 w-20">Index</th>
                  <th className="text-right py-1 w-32">Source</th>
                  <th className="text-right py-1 w-28">Allowance</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r, i) => {
                  const b = BADGE[r.source || 'none']
                  const noIndex = r.source === null
                  const override = r.allowance !== null
                  const pct = override ? r.allowance : eventAllowance
                  return (
                    <tr key={`${r.regId}-${r.playerIndex}-${i}`} className="border-t border-gray-100">
                      <td className="py-1.5 font-semibold">{r.name}</td>
                      <td className="py-1.5 text-gray-600">{lastNames(r.teamName) || '—'}</td>
                      <td className="py-1.5 text-right">{r.handicapIndex ?? '—'}</td>
                      <td className="py-1.5 text-right"><span className={b.cls}>{b.label}</span></td>
                      <td className="py-1.5 text-right">
                        {noIndex ? (
                          <span className="text-gray-400 text-xs">—</span>
                        ) : (
                          <button onClick={() => setPicker({ kind: 'player', row: r })}
                            className="inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1 text-xs font-bold transition"
                            style={override
                              ? { backgroundColor: GOLD, color: GREEN }
                              : { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }}
                            title={override ? 'Per-player override — tap to change' : 'Inherited default — tap to override'}>
                            <span className="opacity-70">{initials(r.name)}</span>
                            <span>{pct}%</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PctGridPicker
        open={!!picker}
        title={picker?.kind === 'player' ? `${picker.row.name} — allowance` : 'Default allowance'}
        subtitle={picker?.kind === 'player'
          ? 'Overrides the event default for this player.'
          : 'Applies to every player without a per-player override.'}
        current={picker?.kind === 'player' ? (picker.row.allowance ?? eventAllowance) : eventAllowance}
        allowDefault={picker?.kind === 'player'}
        onPick={onPick}
        onClose={() => setPicker(null)}
      />
    </div>
  )
}
